import { unionBy } from 'lodash';
import { licensesList, getLabel } from 'Helpers/chargebee/chargebeeUpsellHelpers';
const BASE_URL = '/rest/chargebee';

const createPaymentIntent = (paymentIntent) => {
    return axios.post(`${BASE_URL}/create-payment-intent`, paymentIntent);
};

const fetchCatalogue = async () => {
    const { data: response } = await axios.get(`${BASE_URL}/fetch-addons-plans`);
    const cataloguePlan = [];
    let allAddons = [];

    for (const plan of response) {
        //We explode itemId by _ and we get the third item because its the plan level. ex: 5_20231016_evolution_monthly
        const currentPlanExploded = plan.item_id.split('_');
        const isPricingPV3 = currentPlanExploded[0] === '3';
        const currentPlan = isPricingPV3 ? '3' : currentPlanExploded[2];

        cataloguePlan[currentPlan] = { ...plan };

        const catalogueCurrentPlan = cataloguePlan[currentPlan];
        catalogueCurrentPlan.addons = catalogueCurrentPlan.addons.map((addon) => {
            return { ...addon, ...getLabel(addon) };
        });
        allAddons = unionBy(allAddons, catalogueCurrentPlan.addons, 'id');

        if (catalogueCurrentPlan?.addons) {
            const orderLicenses = [
                'pack_prospecting_sale_full',
                'pack_invoicing_management_full',
                'pack_all_included_full',
            ];
            const orderCashflow = [
                'pack_cashflow_starter_full',
                'pack_cashflow_pro_full',
                'pack_cashflow_business_full',
            ];
            const orderMarketingOptions = [
                'pack_marketing_starter_full',
                'pack_marketing_pro_full',
                'pack_marketing_business_full',
            ];
            const addonsTemp = {};
            addonsTemp.licenses = catalogueCurrentPlan.addons
                .filter((addon) => licensesList.includes(addon.item_id))
                .sort(function (a, b) {
                    return orderLicenses.indexOf(a.item_id) - orderLicenses.indexOf(b.item_id);
                });
            addonsTemp.options = {
                pack_support: catalogueCurrentPlan.addons.find(
                    (addon) => addon.item_id === 'pack_support_full',
                ),
                pack_stock: catalogueCurrentPlan.addons.find(
                    (addon) => addon.item_id === 'pack_stock_full',
                ),
                pack_cashflow: catalogueCurrentPlan.addons
                    .filter((addon) => orderCashflow.includes(addon.item_id))
                    .sort(function (a, b) {
                        return orderCashflow.indexOf(a.item_id) - orderCashflow.indexOf(b.item_id);
                    }),
                pack_marketing: catalogueCurrentPlan.addons
                    .filter((addon) => orderMarketingOptions.includes(addon.item_id))
                    .sort(function (a, b) {
                        return (
                            orderMarketingOptions.indexOf(a.item_id) -
                            orderMarketingOptions.indexOf(b.item_id)
                        );
                    }),
                pack_marketing_email: catalogueCurrentPlan.addons.filter((addon) =>
                    /^pack_marketing_email_(?:pro|business)_full$/g.test(addon.item_id),
                ),
                pack_marketing_dedicated_ip: catalogueCurrentPlan.addons.find(
                    (addon) => addon.item_id === 'pack_marketing_dedicated_ip_full',
                ),
                pack_marketing_custom_domain: catalogueCurrentPlan.addons.find(
                    (addon) => addon.item_id === 'pack_marketing_custom_domain_full',
                ),
                pack_marketing_multi_domains_auth: catalogueCurrentPlan.addons.find(
                    (addon) => addon.item_id === 'pack_marketing_multi_domains_auth_full',
                ),
            };
            if (isPricingPV3) {
                addonsTemp.options = {
                    ...addonsTemp.options,
                    pack_crm: catalogueCurrentPlan.addons.find(
                        (addon) => addon.item_id === 'pack_crm_full',
                    ),
                    pack_invoicing: catalogueCurrentPlan.addons.find(
                        (addon) => addon.item_id === 'pack_invoicing_full',
                    ),
                    pack_management: catalogueCurrentPlan.addons.find(
                        (addon) => addon.item_id === 'pack_management_full',
                    ),
                };
            }
            cataloguePlan[currentPlan].addons = addonsTemp;
        }
    }
    return { allAddons, cataloguePlan };
};
const fetchSubscription = (withoutCharge = false, subscriptionId = null) => {
    let query = withoutCharge ? '?charge=false' : '';
    if (subscriptionId) {
        query += query ? '&' : '?';
        query += `subscription_id=${subscriptionId}`;
    }
    return axios.get(`${BASE_URL}/fetch-customer-subscription${query}`);
};
const fetchSubscriptionEstimation = (data) => {
    const payload = {
        subscription_id: data.id,
        items_prices: data.items_prices.filter((item) => item.quantity > 0),
        discounts: formatSubscriptionDiscounts(data.items_prices, data.discounts),
    };
    return axios.post(`${BASE_URL}/estimate-subscription-update`, payload);
};

const updateSubscription = ({ id, addons, discounts, paymentIntent, billingCycles }) => {
    const payload = {
        subscription_id: id,
        discounts: formatSubscriptionDiscounts(addons, discounts),
        items_prices: addons.filter((addon) => addon.quantity > 0),
        payment_method_type: paymentIntent ? 'card' : 'sepa_instant_transfer',
    };

    if (billingCycles !== null) {
        payload.billing_cycles = billingCycles;
    }

    if (paymentIntent) {
        payload.payment_intent_id = paymentIntent.id;
    }

    return axios.post(`${BASE_URL}/update-subscription`, payload);
};

const updateCustomerDomains = (domains = []) => {
    const payload = domains.map((domain) => {
        return {
            sender_domain: domain.sender,
            sub_domain: domain.subDomain,
        };
    });
    return axios.post(`${BASE_URL}/update-customer-domains`, payload);
};
/**
 * Prepare and format data for Chargebee estimation route.
 * @param {*} items : full list of element in subscription such as plan, licenses and options.
 * @param {*} discounts : full list of discount items
 */
const formatSubscriptionDiscounts = (items, discounts) => {
    const removeAndReplacedItems = items.filter(
        (item) => item.quantity === 0 && item.updatedQuantity <= 0,
    );

    const discountsList = [];

    for (const discount of discounts) {
        const item = removeAndReplacedItems.find(
            (addon) => addon.item_price_id === discount.item_price_id,
        );
        if (item) {
            discountsList.push({
                id: discount.id,
                operation_type: 'remove',
            });
        } else if (discount.shouldReplace) {
            discountsList.push({
                id: discount.id,
                operation_type: 'remove',
            });
            const discountData = {
                ...discount,
                operation_type: 'add',
            };
            if (discount.duration_type === 'limited_period') {
                const discountEndingDate = moment
                    .unix(discount.created_at)
                    .add(discount.period, discount.period_unit);
                const periodRemaining = discountEndingDate.diff(moment(), 'day');

                discountData.period_unit = 'day';
                discountData.period = periodRemaining;
                delete discountData.id;
            }
            discountsList.push(discountData);
        }
    }

    return discountsList;
};

const checkDomainAvailability = async (domain) => {
    try {
        const { status } = await axios.head(
            `${BASE_URL}/check-domain-availability?domain=${domain}`,
        );
        return status;
    } catch (e) {
        if (e.response.status !== 423) {
            throw new Error(e);
        }
        return e.response.status;
    }
};

const cancelSubscriptionCancellation = (subscriptionId) => {
    return axios.post(`${BASE_URL}/cancel-subscription-cancellation`, {
        subscription_id: subscriptionId,
    });
};
export {
    createPaymentIntent,
    fetchSubscription,
    fetchCatalogue,
    fetchSubscriptionEstimation,
    updateSubscription,
    updateCustomerDomains,
    checkDomainAvailability,
    cancelSubscriptionCancellation,
};
